import { apiClient, readCachedData, writeCachedData } from "./client";
import { Admin, AdminRole } from "store/slices/authSlice";

const USERS_LOOKUP_CACHE_KEY = "admin:users:lookup";

export interface LoginResponse {
  token: string;
  admin: Admin;
}

export interface AdminProfile {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateAdminPayload {
  name?: string;
  email?: string;
  password?: string;
  role?: AdminRole;
}

export interface AdminListItem {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminPayload {
  name: string;
  email: string;
  password: string;
  role: AdminRole;
}

export interface AdminTenantSummary {
  id: string;
  name: string;
  planId?: string;
  status?: string;
  createdAt?: string;
}

export interface AdminUser {
  id: string;
  tenantId: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email?: string | null;
  emailVerifiedAt?: string | null;
  tenant?: AdminTenantSummary | null;
}

export interface AdminUserDetail {
  id: string;
  tenantId: string | null;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  email: string | null;
  emailVerifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tenant: AdminTenantDetail | null;
}

export interface AdminTenantDetail {
  id: string;
  name: string;
  planId: string | null;
  status: string;
  createdAt: string;
  plan?: AdminPlan;
}

export interface AdminTenantListItem {
  id: string;
  name: string;
  planId: string | null;
  status: string;
  createdAt: string;
  plan?: AdminPlan | null;
  _count?: { users: number; sessions: number; messages: number };
}

export interface AdminPlan {
  id: string;
  name: string;
  price: number;
  sessionsLimit: number;
  messagesPerDay: number;
  features: Record<string, unknown>;
  createdAt: string;
  _count?: { tenants: number };
}

export interface AdminPromoCode {
  id: string;
  code: string;
  description: string | null;
  discountType: "PERCENT" | "FIXED";
  discountValue: number;
  planId: string | null;
  maxUsesTotal: number | null;
  maxUsesPerUser: number | null;
  usedCount: number;
  startsAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  plan?: AdminPlan | null;
  _count?: { uses: number };
}

export interface CreatePromoCodePayload {
  code: string;
  description?: string;
  discountType: "PERCENT" | "FIXED";
  discountValue: number;
  planId?: string | null;
  maxUsesTotal?: number | null;
  maxUsesPerUser?: number | null;
  startsAt?: string | null;
  expiresAt?: string | null;
  isActive?: boolean;
}

// ─── Tickets ──────────────────────────────────────────────────────────────────

export interface AdminTicketMessage {
  id: string;
  ticketId: string;
  fromAdmin: boolean;
  content: string;
  createdAt: string;
}

export interface AdminTicket {
  id: string;
  tenantId: string;
  assignedTo?: string | null;
  subject: string;
  description: string;
  status: "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
  priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string | null;
  tenant?: { id: string; name: string } | null;
  admin?: { id: string; name: string; email: string } | null;
  messages?: AdminTicketMessage[];
  _count?: { messages: number };
}

export interface UpdateTicketPayload {
  assignedTo?: string | null;
  status?: "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
}

export interface AdminPayment {
  id: string;
  tenantId: string;
  planId: string;
  provider: string;
  providerPaymentId: string;
  amount: number;
  currency: string;
  status: string;
  checkoutUrl: string;
  durationDays: number;
  endDate: string | null;
  lastCheckedAt: string;
  webhookProcessed: boolean;
  createdAt: string;
  plan: { id: string; name: string; price: number };
  tenant?: { id: string; name: string } | null;
}

export interface AdminWebhookDelivery {
  id: string;
  event: string;
  status: string;
  attempts: number;
  lastError: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
  deliveredAt: string | null;
  url: string;
  tenantId: string;
  tenantName: string | null;
  sessionId: string;
}

export interface AdminSubscription {
  id: string;
  tenantId: string;
  planId: string;
  status: string;
  startDate: string;
  endDate: string | null;
  plan: { id: string; name: string; price: number };
  tenant?: { id: string; name: string } | null;
}

export interface AdminUserFull {
  user: AdminUserDetail & { tenant: (AdminTenantDetail & { plan?: AdminPlan }) | null };
  payments: AdminPayment[];
  subscriptions: AdminSubscription[];
  quota: {
    usedToday: number;
    limitPerDay: number;
    remainingToday: number;
  };
}

export interface CreatePlanPayload {
  name: string;
  price: number;
  sessionsLimit: number;
  messagesPerDay: number;
  features?: Record<string, unknown>;
}

export interface AdminTenantSession {
  id?: string;
  tenantId?: string;
  sessionName?: string;
  name?: string;
  phone?: string | null;
  status?: string;
  lastSeen?: string | null;
  authFolderKey?: string;
  qrCode?: string | null;
  lastError?: string | null;
  disconnectedAt?: string | null;
  alertSentAt?: string | null;
  createdAt?: string;
  contactsCount?: number;
  contactsSyncComplete?: boolean;
  [key: string]: unknown;
}

export interface AssignPlanPayload {
  planId: string;
  endDate?: string;
  durationDays?: number;
}

export interface PaymentLinkPayload {
  planId: string;
  adminOverrideAmount?: number;
  adminOverrideCurrency?: string;
  durationDays?: number;
  endDate?: string;
}

export interface PaymentLinkResponse {
  url?: string;
  paymentUrl?: string;
  link?: string;
  [key: string]: unknown;
}

// ─── Referral & Wallet ────────────────────────────────────────────────────────

export interface AdminReferralProgram {
  id: string;
  name: string;
  isActive: boolean;
  referrerRewardValue: number;
  refereeDiscountValue: number;
  eligiblePlanIds: string[]; // [] = tous les plans
  rewardOnEvent: string;
  maxRewardsPerReferrer: number | null;
  linkValidityDays: number;
  minWithdrawalAmount: number;
  createdAt: string;
}

export interface AdminReferral {
  id: string;
  referrerId: string;
  refereeId: string;
  status: "PENDING" | "SUBSCRIBED" | "REWARDED" | "EXPIRED";
  rewardAmount: number | null;
  createdAt: string;
  referrer?: { id: string; name: string } | null;
  referee?: { id: string; name: string } | null;
}

export interface AdminReferralStats {
  totalReferrals: number;
  converted: number;
  rewarded: number;
  totalRewardsPaid: number;
}

export interface AdminWallet {
  id: string;
  tenantId: string;
  balance: number;
  totalEarned: number;
  totalWithdrawn: number;
  currency: string;
  createdAt: string;
  tenant?: { id: string; name: string } | null;
}

export interface AdminWithdrawal {
  id: string;
  tenantId: string;
  walletId: string;
  amount: number;
  status: "PENDING" | "APPROVED" | "PAID" | "REJECTED";
  paymentMethod: string;
  paymentDetails: Record<string, unknown>;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  tenant?: { id: string; name: string } | null;
}

export interface AdminSendEmailPayload {
  subject: string;
  message: string;
  target?: string;
}

export interface AdminSendEmailResponse {
  message?: string;
  queued?: number;
  sent?: number;
  matched?: number;
  failed?: number;
  deduplicated?: number;
  campaignId?: string;
  queue?: string;
  rate?: string;
  estimatedDurationSeconds?: number;
  [key: string]: unknown;
}

export interface AdminUserLookupItem {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  tenantId: string | null;
  tenantName: string;
}

export type AdminUsersLookup = Record<string, AdminUserLookupItem>;

interface UsersResponseShape {
  data?: AdminUser[];
  users?: AdminUser[];
}

export interface AdminLogAdmin {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface AdminLog {
  id?: string;
  tenantId?: string | null;
  adminId?: string | null;
  action?: string;
  entity?: string | null;
  entityId?: string | null;
  metadata?: Record<string, unknown> | null;
  ip?: string | null;
  tenant?: AdminTenantSummary | null;
  admin?: AdminLogAdmin | null;
  actorName?: string;
  actorEmail?: string;
  adminEmail?: string;
  targetType?: string;
  targetId?: string;
  details?: string;
  message?: string;
  createdAt?: string;
  [key: string]: unknown;
}

export type AppConfig = Record<string, unknown>;

function normalizeUsersResponse(response: AdminUser[] | UsersResponseShape): AdminUser[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.users)) return response.users;
  if (Array.isArray(response.data)) return response.data;
  return [];
}

function getSafeName(value?: string | null) {
  return typeof value === "string" ? value.trim() : "";
}

function buildUserLabel(user: AdminUser) {
  const firstName = getSafeName(user.firstName);
  const lastName = getSafeName(user.lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const fallbackName = getSafeName(user.name);
  return fullName || fallbackName || "Utilisateur";
}

function buildUsersLookup(users: AdminUser[]): AdminUsersLookup {
  return users.reduce<AdminUsersLookup>((acc, user) => {
    const firstName = getSafeName(user.firstName);
    const lastName = getSafeName(user.lastName);
    const name = buildUserLabel(user);

    acc[user.id] = {
      id: user.id,
      firstName,
      lastName,
      name,
      email: getSafeName(user.email),
      tenantId: user.tenantId,
      tenantName: getSafeName(user.tenant?.name),
    };

    return acc;
  }, {});
}

function hasUsersLookupData(
  lookup: AdminUsersLookup | null | undefined
): lookup is AdminUsersLookup {
  return Boolean(lookup && typeof lookup === "object" && Object.keys(lookup).length > 0);
}

async function fetchAndCacheUsersLookup() {
  const response = await apiClient.get<AdminUser[] | UsersResponseShape>("/admin/users");
  const users = normalizeUsersResponse(response);
  const lookup = buildUsersLookup(users);
  writeCachedData(USERS_LOOKUP_CACHE_KEY, lookup);
  return lookup;
}

export const authApi = {
  /**
   * Connexion administrateur
   * POST /v1/admin/login
   * Endpoint public → skipAuth: true
   */
  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>("/admin/login", { email, password }, { skipAuth: true }),

  /**
   * Profil de l'admin connecté
   * GET /v1/admin/profile
   */
  getProfile: () => apiClient.get<AdminProfile>("/admin/profile"),

  /**
   * Mise à jour d'un admin
   * PATCH /v1/admin/admins/:id
   */
  updateAdmin: (adminId: string, payload: UpdateAdminPayload) =>
    apiClient.patch<AdminProfile>(`/admin/admins/${adminId}`, payload),

  /**
   * Liste des admins
   * GET /v1/admin/admins
   */
  listAdmins: () => apiClient.get<AdminListItem[]>("/admin/admins"),

  /**
   * Créer un admin
   * POST /v1/admin/admins
   */
  createAdmin: (payload: CreateAdminPayload) =>
    apiClient.post<AdminListItem>("/admin/admins", payload),

  /**
   * Supprimer un admin
   * DELETE /v1/admin/admins/:id
   */
  deleteAdmin: (adminId: string) =>
    apiClient.delete<{ message?: string }>(`/admin/admins/${adminId}`),

  /**
   * Logs d'audit système
   * GET /v1/admin/logs
   */
  listLogs: () =>
    apiClient.get<AdminLog[] | { data?: AdminLog[]; logs?: AdminLog[] }>("/admin/logs"),

  /**
   * Liste des utilisateurs
   * GET /v1/admin/users
   */
  listUsers: () => apiClient.get<AdminUser[] | UsersResponseShape>("/admin/users"),

  /**
   * Détails d'un utilisateur
   * GET /v1/admin/users/:id
   */
  getUser: (userId: string) => apiClient.get<AdminUserFull>(`/admin/users/${userId}`),

  /**
   * Détails d'un tenant
   * GET /v1/admin/tenants/:id
   */
  getTenant: (tenantId: string) => apiClient.get<AdminTenantDetail>(`/admin/tenants/${tenantId}`),

  /**
   * Liste de tous les tenants
   * GET /v1/admin/tenants
   */
  listTenants: () => apiClient.get<AdminTenantListItem[]>("/admin/tenants"),

  /**
   * Modifier un tenant
   * PATCH /v1/admin/tenants/:id
   */
  updateTenant: (tenantId: string, payload: { name?: string; status?: string }) =>
    apiClient.patch<AdminTenantDetail>(`/admin/tenants/${tenantId}`, payload),

  /**
   * Supprimer un tenant
   * DELETE /v1/admin/tenants/:id
   */
  deleteTenant: (tenantId: string) =>
    apiClient.delete<{ message?: string }>(`/admin/tenants/${tenantId}`),

  /**
   * Sessions WhatsApp d'un tenant
   * GET /v1/admin/tenants/:id/sessions
   */
  getTenantSessions: (tenantId: string) =>
    apiClient.get<AdminTenantSession[]>(`/admin/tenants/${tenantId}/sessions`),

  /**
   * Assigner un plan à un tenant
   * PATCH /v1/admin/tenants/:id/plan
   */
  assignPlan: (tenantId: string, payload: AssignPlanPayload) =>
    apiClient.patch<AdminTenantDetail>(`/admin/tenants/${tenantId}/plan`, payload),

  /**
   * Générer un lien de paiement pour un tenant
   * POST /v1/admin/tenants/:id/payment-link
   */
  createPaymentLink: (tenantId: string, payload: PaymentLinkPayload) =>
    apiClient.post<PaymentLinkResponse>(`/admin/tenants/${tenantId}/payment-link`, payload),

  /**
   * Envoyer un email aux utilisateurs
   * POST /v1/admin/email/send
   */
  sendEmail: (payload: AdminSendEmailPayload) =>
    apiClient.post<AdminSendEmailResponse>("/admin/email/send", payload),

  /**
   * Retourne le cache users local/session s'il existe
   */
  getCachedUsersLookup: () => {
    const cached = readCachedData<AdminUsersLookup>(USERS_LOOKUP_CACHE_KEY);
    return hasUsersLookupData(cached) ? cached : null;
  },

  /**
   * Retourne l'index users (cache d'abord)
   */
  getUsersLookup: async () => {
    const cached = readCachedData<AdminUsersLookup>(USERS_LOOKUP_CACHE_KEY);
    if (hasUsersLookupData(cached)) return cached;
    return fetchAndCacheUsersLookup();
  },

  /**
   * Force une mise à jour de l'index users
   */
  refreshUsersLookup: async () => fetchAndCacheUsersLookup(),

  /**
   * Liste de tous les plans
   * GET /v1/admin/plans
   */
  getPlans: () => apiClient.get<AdminPlan[]>("/admin/plans"),

  /**
   * Créer un plan
   * POST /v1/admin/plans
   */
  createPlan: (payload: CreatePlanPayload) => apiClient.post<AdminPlan>("/admin/plans", payload),

  /**
   * Modifier un plan
   * PATCH /v1/admin/plans/:id
   */
  updatePlan: (planId: string, payload: Partial<CreatePlanPayload>) =>
    apiClient.patch<AdminPlan>(`/admin/plans/${planId}`, payload),

  /**
   * Supprimer un plan
   * DELETE /v1/admin/plans/:id
   */
  deletePlan: (planId: string) => apiClient.delete<{ message?: string }>(`/admin/plans/${planId}`),

  /**
   * Liste de tous les paiements
   * GET /v1/admin/payments
   */
  getPayments: () => apiClient.get<AdminPayment[]>("/admin/payments"),

  /**
   * Liste des tentatives de livraison webhook, tous tenants confondus
   * GET /v1/admin/webhooks/deliveries
   */
  getWebhookDeliveries: (filters?: {
    tenantId?: string;
    sessionId?: string;
    event?: string;
    status?: string;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.tenantId) params.set("tenantId", filters.tenantId);
    if (filters?.sessionId) params.set("sessionId", filters.sessionId);
    if (filters?.event) params.set("event", filters.event);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.limit) params.set("limit", String(filters.limit));
    const qs = params.toString();
    return apiClient.get<AdminWebhookDelivery[]>(
      `/admin/webhooks/deliveries${qs ? `?${qs}` : ""}`,
    );
  },

  /**
   * Supprimer un utilisateur
   * DELETE /v1/admin/users/:id
   */
  deleteUser: (userId: string) => apiClient.delete<{ message?: string }>(`/admin/users/${userId}`),

  /**
   * Liste des codes promo
   * GET /v1/admin/promo-codes
   */
  listPromoCodes: () => apiClient.get<AdminPromoCode[]>("/admin/promo-codes"),

  /**
   * Créer un code promo
   * POST /v1/admin/promo-codes
   */
  createPromoCode: (payload: CreatePromoCodePayload) =>
    apiClient.post<AdminPromoCode>("/admin/promo-codes", payload),

  /**
   * Modifier un code promo
   * PATCH /v1/admin/promo-codes/:id
   */
  updatePromoCode: (id: string, payload: Partial<CreatePromoCodePayload>) =>
    apiClient.patch<AdminPromoCode>(`/admin/promo-codes/${id}`, payload),

  /**
   * Supprimer un code promo
   * DELETE /v1/admin/promo-codes/:id
   */
  deletePromoCode: (id: string) =>
    apiClient.delete<{ message?: string }>(`/admin/promo-codes/${id}`),

  /**
   * Liste de tous les tickets de support
   * GET /v1/admin/tickets
   */
  listTickets: () => apiClient.get<AdminTicket[]>("/admin/tickets"),

  /**
   * Détail d'un ticket avec messages
   * GET /v1/admin/tickets/:id
   */
  getTicket: (ticketId: string) => apiClient.get<AdminTicket>(`/admin/tickets/${ticketId}`),

  /**
   * Admin répond à un ticket
   * POST /v1/admin/tickets/:id/messages
   */
  addAdminMessage: (ticketId: string, content: string) =>
    apiClient.post<AdminTicketMessage>(`/admin/tickets/${ticketId}/messages`, { content }),

  /**
   * Mettre à jour un ticket (statut, assignation)
   * PATCH /v1/admin/tickets/:id
   */
  updateTicket: (ticketId: string, payload: UpdateTicketPayload) =>
    apiClient.patch<AdminTicket>(`/admin/tickets/${ticketId}`, payload),

  // ─── Referral ──────────────────────────────────────────────────────────────

  /**
   * Programme de parrainage (config)
   * GET /v1/admin/referral-program
   */
  getReferralProgram: () => apiClient.get<AdminReferralProgram>("/admin/referral-program"),

  /**
   * Liste tous les plans disponibles (pour le sélecteur parrainage)
   * GET /v1/admin/plans
   */
  listPlans: () => apiClient.get<{ id: string; name: string; price: number }[]>("/admin/plans"),

  /**
   * Mettre à jour la config du programme
   * PATCH /v1/admin/referral-program
   */
  updateReferralProgram: (payload: Partial<AdminReferralProgram>) =>
    apiClient.patch<AdminReferralProgram>("/admin/referral-program", payload),

  /**
   * Liste de tous les parrainages
   * GET /v1/admin/referrals
   */
  listReferrals: () => apiClient.get<AdminReferral[]>("/admin/referrals"),

  /**
   * Stats parrainage global
   * GET /v1/admin/referrals/stats
   */
  getReferralStats: () => apiClient.get<AdminReferralStats>("/admin/referrals/stats"),

  // ─── Wallets ───────────────────────────────────────────────────────────────

  /**
   * Liste de tous les wallets
   * GET /v1/admin/wallets
   */
  listWallets: () => apiClient.get<AdminWallet[]>("/admin/wallets"),

  /**
   * Liste de toutes les demandes de retrait
   * GET /v1/admin/withdrawals
   */
  listWithdrawals: () => apiClient.get<AdminWithdrawal[]>("/admin/withdrawals"),

  /**
   * Approuver un retrait
   * PATCH /v1/admin/withdrawals/:id/approve
   */
  approveWithdrawal: (id: string) =>
    apiClient.patch<AdminWithdrawal>(`/admin/withdrawals/${id}/approve`, {}),

  /**
   * Marquer un retrait comme payé
   * PATCH /v1/admin/withdrawals/:id/paid
   */
  markWithdrawalPaid: (id: string) =>
    apiClient.patch<AdminWithdrawal>(`/admin/withdrawals/${id}/paid`, {}),

  /**
   * Rejeter un retrait
   * PATCH /v1/admin/withdrawals/:id/reject
   */
  rejectWithdrawal: (id: string, reason?: string) =>
    apiClient.patch<AdminWithdrawal>(`/admin/withdrawals/${id}/reject`, { reason }),

  /**
   * Créditer le wallet d'un tenant
   * POST /v1/admin/wallets/:tenantId/credit
   */
  creditWallet: (tenantId: string, amount: number, description?: string) =>
    apiClient.post(`/admin/wallets/${tenantId}/credit`, { amount, description }),

  /**
   * Débiter le wallet d'un tenant
   * POST /v1/admin/wallets/:tenantId/debit
   */
  debitWallet: (tenantId: string, amount: number, description?: string) =>
    apiClient.post(`/admin/wallets/${tenantId}/debit`, { amount, description }),

  // ─── Config ────────────────────────────────────────────────────────────────

  /**
   * Lire la configuration plateforme
   * GET /v1/admin/config
   */
  getConfig: () => apiClient.get<AppConfig>("/admin/config"),

  /**
   * Mettre à jour la configuration
   * PATCH /v1/admin/config
   */
  updateConfig: (entries: Record<string, unknown>) =>
    apiClient.patch<AppConfig>("/admin/config", { entries }),
};
